import { Link } from 'react-router-dom';
import { useAuditLogQuery, type AuditLogEntryDto } from '../queries/use-audit-log.js';
import { useMeQuery } from '../queries/use-me.js';

export function Home(): JSX.Element {
  const meQuery = useMeQuery();
  const isAdmin = (meQuery.data?.roles ?? []).includes('Admin');

  return (
    <section className="mx-auto w-full max-w-5xl px-4 py-8">
      <h1 className="mb-2 text-3xl font-bold text-gray-900 dark:text-gray-100">Dashboard</h1>
      <p className="mb-6 text-gray-600 dark:text-gray-400">
        Tenant configuration for the WorkFocus FCOE platform.
      </p>

      {isAdmin ? (
        <RecentActivity />
      ) : (
        <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-gray-800">
          <h2 className="mb-2 text-lg font-semibold text-gray-800 dark:text-gray-200">
            Pick a tenant to start
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Open the <span className="font-medium">Clients</span> tab and choose a tenant to
            configure licenses, OAuth apps, Workfront connections, and admins.
          </p>
        </div>
      )}
    </section>
  );
}

/**
 * Global recent-activity feed — Admin-only. Operators/Viewers see the
 * tenant-scoped feed inside each Client Detail page (a future slice
 * adds that). Limit is 25 to keep the dashboard tight.
 */
function RecentActivity(): JSX.Element {
  const { data, isPending, isError, error } = useAuditLogQuery({ limit: 25 });

  return (
    <div className="rounded-lg border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
      <header className="flex items-center justify-between border-b border-gray-200 px-6 py-4 dark:border-gray-700">
        <div>
          <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-200">
            Recent activity
          </h2>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            State changes across all tenants — newest first.
          </p>
        </div>
      </header>

      {isPending ? (
        <p className="px-6 py-6 text-sm text-gray-500 dark:text-gray-400" role="status">
          Loading recent activity…
        </p>
      ) : isError ? (
        <p className="px-6 py-6 text-sm text-red-600 dark:text-red-400" role="alert">
          {error instanceof Error ? error.message : 'Failed to load recent activity.'}
        </p>
      ) : data && data.length === 0 ? (
        <p className="px-6 py-6 text-sm text-gray-500 dark:text-gray-400">
          No activity yet. Audit rows land here as soon as someone creates or modifies a tenant.
        </p>
      ) : (
        <ul className="divide-y divide-gray-200 dark:divide-gray-700">
          {data?.map((entry) => (
            <ActivityRow key={entry.id} entry={entry} />
          ))}
        </ul>
      )}
    </div>
  );
}

function ActivityRow({ entry }: { entry: AuditLogEntryDto }): JSX.Element {
  return (
    <li className="px-6 py-3 text-sm">
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-gray-900 dark:text-gray-100">
          <span className="font-medium">{actorLabel(entry)}</span>{' '}
          <span className="text-gray-700 dark:text-gray-300">{describeAction(entry)}</span>
        </span>
        <time
          className="shrink-0 text-xs text-gray-500 dark:text-gray-400"
          dateTime={entry.createdAt}
          title={entry.createdAt}
        >
          {formatRelative(entry.createdAt)}
        </time>
      </div>
      {entry.clientId ? (
        <Link
          to={`/clients/${entry.clientId}`}
          className="text-xs text-blue-600 hover:underline dark:text-blue-400"
        >
          {tenantLabel(entry)}
        </Link>
      ) : (
        <span className="text-xs text-gray-500 dark:text-gray-400">Global action</span>
      )}
    </li>
  );
}

function actorLabel(entry: AuditLogEntryDto): string {
  return entry.actorDisplayName ?? entry.actorEmail ?? entry.actorOid;
}

function tenantLabel(entry: AuditLogEntryDto): string {
  const name = entry.metadata['name'];
  if (typeof name === 'string') return name;
  return entry.clientId ?? '';
}

/**
 * Maps a (object, action) pair to a human-readable phrase. Falls back
 * to the raw values for any combination this widget hasn't been
 * taught about — better to render something verbose than swallow
 * unknown audit entries.
 */
function describeAction(entry: AuditLogEntryDto): string {
  const { object, action, metadata } = entry;
  if (object === 'client' && action === 'create') return 'created a tenant';
  if (object === 'client' && action === 'delete') return 'deleted a tenant';
  if (object === 'tenant-admin' && action === 'grant') {
    const role = typeof metadata['role'] === 'string' ? metadata['role'] : 'admin';
    const auto = metadata['autoGrant'] === true ? ' (creator auto-grant)' : '';
    return `granted ${role}${auto} to ${describeTarget(entry)}`;
  }
  if (object === 'tenant-admin' && action === 'revoke') {
    const role = typeof metadata['role'] === 'string' ? metadata['role'] : 'admin';
    return `revoked ${role} from ${describeTarget(entry)}`;
  }
  if (object === 'tenant-admin' && action === 'update') {
    const from = typeof metadata['from'] === 'string' ? metadata['from'] : '?';
    const to = typeof metadata['to'] === 'string' ? metadata['to'] : '?';
    return `changed ${describeTarget(entry)} from ${from} to ${to}`;
  }
  return `${action}d ${object}`;
}

function describeTarget(entry: AuditLogEntryDto): string {
  const email = entry.metadata['email'];
  if (typeof email === 'string') return email;
  return entry.targetId ?? 'someone';
}

const RELATIVE = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });
const UNITS: Array<[Intl.RelativeTimeFormatUnit, number]> = [
  ['second', 1],
  ['minute', 60],
  ['hour', 60 * 60],
  ['day', 60 * 60 * 24],
  ['week', 60 * 60 * 24 * 7],
  ['month', 60 * 60 * 24 * 30],
  ['year', 60 * 60 * 24 * 365],
];

function formatRelative(iso: string): string {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return iso;
  const deltaSec = Math.round((t - Date.now()) / 1000);
  const absSec = Math.abs(deltaSec);
  let chosenUnit: Intl.RelativeTimeFormatUnit = 'second';
  let chosenScale = 1;
  for (const [unit, scale] of UNITS) {
    if (absSec >= scale) {
      chosenUnit = unit;
      chosenScale = scale;
    }
  }
  return RELATIVE.format(Math.round(deltaSec / chosenScale), chosenUnit);
}
